import moment from 'moment';
import {
  CONFIG,
  AS_OF,
  PROPERTY,
  LEAD_BUCKETS,
  Q,
  LEVERS,
  RULES,
  RES_FIELDS,
  PROMO_OWNERS,
  PROMO_NAMES,
  PROMO_FENCES,
  PROMO_TYPE_LABEL,
  PROMO_RULES,
  CHANNELS,
  normalizeChannelName,
  enrichRatePlans,
  toCalendarYMD,
  formatCalendarDate
} from './yieldRadarData.js';
import { commonService } from '../../../../../utils/commonService.js';

let cachedPlansRes = null;
let cachedActivePlans = null;

function getActivePlans(activeRes) {
  if (cachedPlansRes === activeRes && cachedActivePlans) {
    return cachedActivePlans;
  }
  cachedPlansRes = activeRes;
  cachedActivePlans = enrichRatePlans([], activeRes || []);
  return cachedActivePlans;
}

let cachedResKey = null;
let cachedResByDateAndPlan = null;

function getResByDateAndPlan(activeRes, activeDates) {
  const key = `${activeRes?.length || 0}_${activeDates?.length || 0}`;
  if (cachedResKey === key && cachedResByDateAndPlan) {
    return cachedResByDateAndPlan;
  }
  cachedResKey = key;
  const dateList = activeDates.map(d => d.d);
  const map = new Map();
  if (Array.isArray(activeRes) && activeRes.length > 0) {
    for (let ri = 0; ri < activeRes.length; ri++) {
      const r = activeRes[ri];
      if (r.is_stly || r.isStly) continue;
      const st = String(r.status || '').trim().toLowerCase();
      const isActive = !st || st === 'confirmed' || st === 'in-house' || st === 'inhouse' || st === 'checked-in' || st === 'active';
      if (isActive) {
        const arr = toCalendarYMD(r.arrival_date || r.arrival);
        const dep = toCalendarYMD(r.departure_date || r.depart);
        const c = r.rate_plan_code || r.plan;
        const ch = normalizeChannelName(r.channel || 'DIRECT');
        if (arr && c) {
          for (let di = 0; di < dateList.length; di++) {
            const d = dateList[di];
            if (arr <= d && (dep ? d < dep : d === arr)) {
              let planMap = map.get(d);
              if (!planMap) {
                planMap = {};
                map.set(d, planMap);
              }
              planMap[c] = (planMap[c] || 0) + 1;
              const comboKey = `${c}|${ch}`;
              planMap[comboKey] = (planMap[comboKey] || 0) + 1;
            }
          }
        }
      }
    }
  }
  cachedResByDateAndPlan = map;
  return map;
}

export function createRadarModel(overrides = {}, root, customDates = null, asOf = null, customReservations = null, customCapacity = null, customPropertyCode = null){
  const activeDates = Array.isArray(customDates) ? customDates : [];
  const activeRes = Array.isArray(customReservations) ? customReservations : [];
  const activePlans = getActivePlans(activeRes);
  const activeAsOf = toCalendarYMD(asOf) || AS_OF;
  const activeCapacity = Number(customCapacity) || CONFIG.capacity || 126;
  const activeProperty = customPropertyCode || PROPERTY || '';

  let SCR = null;
  const resCache = {};
  const analysisCache = {};
  const resByDateAndPlan = getResByDateAndPlan(activeRes, activeDates);
  const P1 = v => (Math.round(v * 10) / 10) + '%';

  const fmt$ = n => {
    const num = Number(n);
    if (isNaN(num) || !Number.isFinite(num)) return commonService.formateAmount(0);
    return commonService.formateAmount(num);
  };

  const fmtRoom = n => {
    const num = Number(n);
    if (isNaN(num) || !Number.isFinite(num)) return commonService.formateRoom(0);
    return commonService.formateRoom(num);
  };

  const r2 = n => Math.round(n * 100) / 100;

  const signedRoom = n => {
    const num = Object.is(n, -0) || Math.abs(n) === 0 ? 0 : Number(n);
    if (isNaN(num) || num === 0) return '0';
    return (num > 0 ? '+' : '-') + commonService.formateRoom(Math.abs(num));
  };

  const sigs = i => {
    const safeI = (i >= 0 && i < activeDates.length) ? i : 0;
    const dateRecord = activeDates[safeI] || {};
    return Object.assign({}, dateRecord, overrides[dateRecord.d] || overrides[i] || overrides[safeI] || {});
  };


  const dow = s => {
    const ymd = toCalendarYMD(s);
    if (!ymd) return '';
    const m = moment(ymd, 'YYYY-MM-DD');
    return m.isValid() ? m.format('ddd') : '';
  };

  const md = s => formatCalendarDate(s, 'MMM D');

  const longDate = s => formatCalendarDate(s, 'dddd, MMMM D, YYYY');

  const cssVar = v => {
    if (root && typeof getComputedStyle === 'function') {
      try {
        const val = getComputedStyle(root).getPropertyValue(v)?.trim();
        if (val) return val;
      } catch (e) {}
    }
    const fallback = {
      '--muted': '#888888',
      '--line': 'rgba(0,0,0,0.1)',
      '--text': '#222222',
      '--gold': '#bfa15f',
      '--panel-2': '#f9fafb',
      '--demand-bg': 'rgba(239,68,68,0.1)',
      '--demand': '#ef4444',
      '--healthy': '#10b981',
      '--under': '#3b82f6',
      '--resist': '#f59e0b',
    };
    return fallback[v] || '';
  };

  const esc = s => String(s).replace(/[&<>"]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[c]));

  function seeded(str){
    let h = 1779033703 ^ str.length;
    for (const ch of str){
      h = Math.imul(h ^ ch.charCodeAt(0), 3432918353);
      h = (h << 13) | (h >>> 19);
    }
    return () => {
      h = Math.imul(h ^ (h >>> 16), 2246822507);
      h = Math.imul(h ^ (h >>> 13), 3266489909);
      return ((h ^= h >>> 16) >>> 0) / 4294967296;
    };
  }

  const planKey = p => `${p.code}|${p.channel}`;

  const paceIndex = s => (Number(s.stly) > 0 ? (Number(s.otb) || 0) / Number(s.stly) : (Number(s.otb) > 0 ? 2 : 1));

  const adrIndex = s => (Number(s.stlyAdr) > 0 ? (Number(s.adr) || 0) / Number(s.stlyAdr) : (Number(s.adr) > 0 ? 2 : 1));

  function quadrant(s){
    const p = paceIndex(s) >= CONFIG.paceAheadAt, a = adrIndex(s) >= CONFIG.adrAheadAt;
    return p ? (a ? 'healthy' : 'under') : (a ? 'resist' : 'demand');
  }

  function windowOf(dba){
    const w = CONFIG.windows;
    return dba > w.priceOnlyAbove ? 'price' : dba > w.deepAbove ? 'deep' : dba > w.closuresAbove ? 'closures' : 'closein';
  }

  const tierOf = s => CONFIG.hurdleTiers.find(t => (s?.fcst || 0) >= t.minFcst) || CONFIG.hurdleTiers[CONFIG.hurdleTiers.length - 1];

  const hurdleOf = s => {
    const b = (s?.bar > 0) ? s.bar : ((s?.adr > 0) ? s.adr : 0);
    return Math.round(b * (tierOf(s)?.pct ?? 0.55));
  };

  function barMove(s, q){
    const cap = Math.round(s.bar * CONFIG.maxBarChangePct);
    if (q === 'under'){
      const c = CONFIG.barRaise;
      return Math.min(cap, Math.max(c.min, Math.round((s.stlyAdr - s.adr) * c.adrGapWeight + (s.fcst - c.fcstPivot) * c.fcstWeight)));
    }
    if (q === 'healthy'){
      const c = CONFIG.healthyRaise;
      return s.fcst >= c.minFcst ? Math.min(cap, Math.max(c.min, Math.round(s.bar * c.pct))) : 0;
    }
    if (q === 'resist'){
      const c = CONFIG.barLower;
      return -Math.min(Math.round(s.bar * c.maxPct), Math.max(c.min, Math.round((s.adr - s.stlyAdr) * c.adrGapWeight)));
    }
    return 0;
  }

  function tightenOf(s, q){
    return q === 'under' || (q === 'healthy' && s.fcst >= CONFIG.healthyTightenMinFcst) || s.fcst >= CONFIG.anyTightenMinFcst;
  }

  const sellOf = (p, bar) => p.fixed != null ? p.fixed : Math.round(bar * (1 - (p.disc || 0)) + (p.add || 0));

  const netOf = (p, bar) => sellOf(p, bar) * (1 - p.comm) - (p.cost || 0);

  function confidenceOf(s){
    const c = CONFIG.lowConfidence;
    return s.dba > c.dbaAbove || Math.abs(paceIndex(s) - 1) < c.paceBand ? 'MEDIUM' : 'HIGH';
  }

  function evaluatePlan(p, s, ctx){
    const {q, w, h, mv, tighten} = ctx, sell = sellOf(p, s.bar), net = netOf(p, s.bar);
    const vs = `Net ${fmt$(net)} ${net >= h ? '≥' : '<'} hurdle ${fmt$(h)}`;
    const trace = [];
    const step = (qtext, val, hit, f) => trace.push({q:qtext, val, hit, f});
    const out = (rule, act, cls, reason, actionable = false, newRate = null) => ({rule, act, cls, reason, actionable, newRate, trace, sell, net});

    step('Negotiated or group rate?', p.protected ? 'Yes' : 'No', !!p.protected, `type = ${p.type}`);
    if (p.protected) return out('R1','Protected','b-neutral','Contractual rate, never closed');
    if (p.minDba){
      const ok = s.dba >= p.minDba;
      step('Booking window still open?', `${commonService.formateRoom(s.dba)} ${ok?'≥':'<'} ${commonService.formateRoom(p.minDba)} days`, !ok, 'days_before_arrival ≥ min_booking_window');
      if (!ok) return out('R2','Window passed','b-neutral',`Booking window ended at ${commonService.formateRoom(p.minDba)} days out`);
    }
    step('BAR plan?', p.type === 'BAR' ? 'Yes' : 'No', p.type === 'BAR', 'BAR moves are set once on the lead BAR row');
    if (p.type === 'BAR'){
      step('BAR move for this date', mv === 0 ? 'None' : (mv > 0 ? '+' : '') + fmt$(mv), true, 'see Decision trace step 7');
      if (mv === 0) return out('R5','Hold','b-keep', q === 'demand' ? "Don't chase rate down; fix demand with mix" : 'Rate is in line with demand');
      if (p.isLeadBar){
        const capped = Math.abs(mv) === Math.round(s.bar * CONFIG.maxBarChangePct);
        return out('R3', `${mv>0?'Raise':'Lower'} BAR ${mv>0?'+':'−'}${fmt$(Math.abs(mv))}`, mv>0?'b-raise':'b-lower', `New BAR ${fmt$(s.bar+mv)} across all channels${capped?' (capped at 12%)':''}`, true, s.bar + mv);
      }
      return out('R4','Follows BAR','b-neutral',`Derived rate moves to ${fmt$(s.bar+mv)}`, false, s.bar + mv);
    }
    step('Currently closed?', p.closed ? 'Yes' : 'No', !!p.closed, 'current channel-manager status');
    if (p.closed){
      const soft = q === 'demand' || q === 'resist', reopen = soft && w !== 'price' && net >= h;
      step('Soft date and net clears hurdle?', reopen ? 'Yes' : 'No', reopen, `quadrant ∈ {resistance, demand gap} and ${vs}`);
      return reopen ? out('R6','Reopen','b-reopen',`Soft date. ${vs}`, true) : out('R7','Stay closed','b-neutral', net < h ? vs : 'Demand does not need this channel');
    }
    step('Lever window allows closures?', w === 'price' ? 'No (90+ days)' : w === 'deep' ? `Only if forecast ≥ ${CONFIG.deepWindowCloseMinFcst}%` : 'Yes', w === 'price', `window = ${LEVERS[w]?.id || w}`);
    if (w === 'price') return out('R8','Keep open','b-keep','Too early to close plans');
    const canClose = w === 'deep' ? s.fcst >= CONFIG.deepWindowCloseMinFcst : tighten;
    step('Date needs tightening?', canClose ? 'Yes' : 'No', false, w === 'deep' ? `forecast ${P1(s.fcst)} vs ${CONFIG.deepWindowCloseMinFcst}%` : 'underpriced, or healthy ≥ 90% forecast, or any ≥ 95% forecast');
    step('Net rate vs hurdle', `${fmt$(net)} ${net >= h ? '≥' : '<'} ${fmt$(h)}`, canClose && net < h, `net = sell × (1 − ${p.comm}) − ${p.cost||0}`);
    if (canClose && net < h){
      const L = losCheck(p, ctx, net, h);
      step('Length-of-stay check (R9a)', L.n ? `MinLOS ${commonService.formateRoom(L.n)} clears by ${fmt$(L.margin)}` : L.why, !!L.n, `net ≥ ${Math.round(CONFIG.los.minNetPctOfHurdle*100)}% of hurdle and Σ(net − hurdle) over a 2–3 night window ≥ 0`);
      if (L.n) {
        const o = out('R9a', `Set MinLOS ${commonService.formateRoom(L.n)}`, 'b-los', `${commonService.formateRoom(L.n)}-night stay clears hurdles by ${fmt$(L.margin)}`, true);
        o.minLos = L.n;
        o.margins = L.m;
        return o;
      }
      const o9 = out('R9', w === 'closein' ? 'Close to arrival' : 'Close','b-close', vs, true);
      o9.margins = L.m || null;
      o9.losWhy = L.why;
      return o9;
    }
    return out('R10','Keep open','b-keep', vs);
  }

  function losCheck(p, ctx, net, h){
    const m = {};
    [-2,-1,0,1,2].forEach(o => {
      const nb = ctx.nb[o + 2];
      m[o] = nb ? netOf(p, nb.bar) - nb.h : null;
    });
    if (!CONFIG.los.enabled) return {n:null, why:'LOS check disabled', m};
    if (net < CONFIG.los.minNetPctOfHurdle * h) return {n:null, why:`Net is below ${Math.round(CONFIG.los.minNetPctOfHurdle*100)}% of the hurdle, so the discount is too deep for MinLOS.`, m};
    const sum = os => os.every(o => m[o] != null) ? os.reduce((t, o) => t + m[o], 0) : -Infinity;
    const b2 = Math.max(sum([-1,0]), sum([0,1])), b3 = Math.max(sum([-2,-1,0]), sum([-1,0,1]), sum([0,1,2]));
    if (b2 >= 0) return {n:2, margin:b2, m};
    if (CONFIG.los.maxMinLos >= 3 && b3 >= 0) return {n:3, margin:b3, m};
    return {n:null, why:'No 2–3 night stay clears the hurdles', m};
  }

  function analyse(i){
    const safeI = (i >= 0 && i < activeDates.length) ? i : 0;
    if (analysisCache[safeI]) return analysisCache[safeI];
    const s = sigs(safeI), q = quadrant(s), w = windowOf(s.dba), h = hurdleOf(s), mv = barMove(s, q), tighten = tightenOf(s, q);
    const nb = [-2,-1,0,1,2].map(o => {
      const j = safeI + o;
      if (j < 0 || j >= activeDates.length) return null;
      const t = sigs(j);
      return {d:t.d, bar:t.bar, h:hurdleOf(t)};
    });
    const otbRooms = Math.round(s.otb / 100 * activeCapacity), ctx = {q, w, h, mv, tighten, nb};
    const dateResByPlan = resByDateAndPlan.get(s.d) || {};
    const hasResData = (Array.isArray(activeRes) && activeRes.length > 0 && resByDateAndPlan.size > 0);

    // If live reservation data exists, show only the rate plans that actually have data on this date
    let datePlans = activePlans;
    if (hasResData) {
      const bookedPlans = activePlans.filter(p => {
        const count = dateResByPlan[`${p.code}|${p.channel}`] ?? dateResByPlan[p.code];
        return (count > 0) || (p.isLeadBar && mv !== 0);
      });
      if (bookedPlans.length > 0) {
        datePlans = bookedPlans;
      }
    }

    const rows = datePlans.map(p => {
      const bookedRooms = dateResByPlan[`${p.code}|${p.channel}`] ?? dateResByPlan[p.code];
      const rooms = bookedRooms !== undefined ? bookedRooms : (hasResData ? 0 : Math.round(p.share * otbRooms));
      return Object.assign({p, key:planKey(p), rooms}, evaluatePlan(p, s, ctx));
    });
    const result = {i: safeI, s, q, w, h, mv, tighten, rows, otbRooms, conf:confidenceOf(s)};
    analysisCache[safeI] = result;
    return result;
  }

  function screen(){
    const c = CONFIG.screener, list = activeDates.map((_, i) => {
      const a = analyse(i), s = a.s, rooms = v => Math.round(v / 100 * activeCapacity);
      const closures = a.rows.filter(r => r.rule === 'R9' || r.rule === 'R9a').length;
      const reopens = a.rows.filter(r => r.rule === 'R6').length;
      const actions = closures + reopens + (a.mv !== 0 ? 1 : 0);
      const effectiveAdr = (s.adr > 0) ? s.adr : ((s.bar > 0) ? s.bar : 120);
      const stake = (a.q === 'under' || a.q === 'healthy')
        ? Math.abs(a.mv) * Math.max(0, (overrides[i] || overrides[s.d] ? rooms(s.fcst) : s.fcR) - rooms(s.otb))
        : Math.max(0, rooms(s.stly) - rooms(s.otb)) * effectiveAdr;
      return {
        i, a, actions, stake,
        sPace: Math.min(1, Math.abs(paceIndex(s) - 1) / c.paceCap),
        sAdr: Math.min(1, Math.abs(adrIndex(s) - 1) / c.adrCap),
        sComp: Math.max(0, s.fcst - c.compStart) / (100 - c.compStart),
        sUrg: c.urgency[a.w] ?? 0.5,
        sEv: s.ev ? 1 : 0
      };
    });
    const maxStake = Math.max(...list.map(x => x.stake), 1);
    list.forEach(x => {
      x.sRev = x.stake / maxStake;
      x.score = Math.round((c.wPace*x.sPace + c.wAdr*x.sAdr + c.wComp*x.sComp + c.wDollar*x.sRev + c.wUrg*x.sUrg + c.wEvent*x.sEv) * (x.actions === 0 ? c.noActionFactor : 1) * 10) / 10;
    });
    const order = list.slice().sort((m, n) => n.score - m.score || m.i - n.i);
    order.forEach((x, k) => {
      x.rank = k + 1;
      x.onStrip = x.rank <= c.topN;
    });
    SCR = {list, order};
    return SCR;
  }

  const why = x => {
    const q = x.a.q;
    return q === 'under'
      ? 'Pace ahead, ADR behind: raise rate, close discounts'
      : q === 'healthy'
        ? (x.actions ? 'Compressing: protect close-in inventory' : 'Healthy, no action')
        : q === 'resist'
          ? 'ADR ahead, pace behind: check compset, value-add'
          : 'Pace and ADR behind: use mix and promotions';
  };

  const maxDba = activeDates.length > 0 ? Math.max(...activeDates.map(d => d.dba || 0)) : 0;
  const horizonDays = Math.max(activeDates.length, maxDba + 1);
  const horizonText = horizonDays >= 90 ? `${horizonDays}+ days` : `${horizonDays} days`;

  const SHOW = Math.max(90, maxDba);
  const H = Math.max(180, SHOW * 2);

  function curveFn(final, at, d){
    final = Math.max(final, at + 1);
    const p = d >= 1 ? Math.max(.35, Math.log(at / final) / Math.log(1 - d / H)) : 1;
    return x => final * Math.pow(Math.max(0, 1 - x / H), p);
  }

  function bookingCurves(a){
    if (a && a._bookingCurves) return a._bookingCurves;
    const s = a.s, rnd = seeded(s.d);
    const stlyDate = moment(s.d, 'YYYY-MM-DD').subtract(364, 'days').format('YYYY-MM-DD');

    const tyRes = (activeRes || []).filter(x => {
      if (x.is_stly || x.isStly) return false;
      const arr = toCalendarYMD(x.arrival_date || x.arrival);
      const dep = toCalendarYMD(x.departure_date || x.depart);
      return arr && (arr <= s.d && (dep ? s.d < dep : s.d === arr));
    });

    const stlyRes = (activeRes || []).filter(x => {
      if (!x.is_stly && !x.isStly) return false;
      const arr = toCalendarYMD(x.arrival_date || x.arrival);
      const dep = toCalendarYMD(x.departure_date || x.depart);
      const alignedArr = toCalendarYMD(x.aligned_arrival_date);
      const alignedDep = toCalendarYMD(x.aligned_departure_date);
      const matchOriginal = arr && (arr <= stlyDate && (dep ? stlyDate < dep : stlyDate === arr));
      const matchAligned = alignedArr && (alignedArr <= s.d && (alignedDep ? s.d < alignedDep : s.d === alignedArr));
      return matchOriginal || matchAligned;
    });

    const actualStlyRooms = stlyRes.reduce((sum, r) => sum + (Number(r.rooms || r.room_count) || 1), 0);
    const actualStlyOcc = (activeCapacity > 0 && actualStlyRooms > 0) ? (actualStlyRooms / activeCapacity) * 100 : s.stly;

    const baseStlyFinal = Math.min(99, Math.max(actualStlyOcc + 2, Math.round(actualStlyOcc * s.fcst / Math.max(1, s.otb))));
    const stlyFinal = actualStlyRooms > 0 ? Math.max(actualStlyOcc, baseStlyFinal) : baseStlyFinal;

    const fT = curveFn(s.fcst, s.otb, s.dba), fL = curveFn(stlyFinal, actualStlyOcc, s.dba);
    const ty = [], fc = [], ly = [];

    // Precalculate cumulative rooms across lead days in O(N + SHOW) instead of O(N * SHOW)
    const computeCumulativeOcc = (resList) => {
      if (!resList || !resList.length || !activeCapacity) return null;
      const roomsAtLead = new Float64Array(SHOW + 1);
      for (let ri = 0; ri < resList.length; ri++) {
        const r = resList[ri];
        const bkd = toCalendarYMD(r.booking_date || r.booked);
        const arr = toCalendarYMD(r.arrival_date || r.arrival);
        const lead = (bkd && arr) ? Math.max(0, diffDays(bkd, arr)) : 0;
        const bucket = Math.min(SHOW, lead);
        roomsAtLead[bucket] += Number(r.rooms || r.room_count) || 1;
      }
      let runningRooms = 0;
      const occAtX = new Float64Array(SHOW + 1);
      for (let x = SHOW; x >= 0; x--) {
        runningRooms += roomsAtLead[x];
        occAtX[x] = (runningRooms / activeCapacity) * 100;
      }
      return occAtX;
    };

    const realLyOccArray = stlyRes.length > 0 ? computeCumulativeOcc(stlyRes) : null;
    const realTyOccArray = tyRes.length > 0 ? computeCumulativeOcc(tyRes) : null;

    let lastT = 0, lastL = 0;
    for (let x = SHOW; x >= 0; x--){
      let vl;
      if (realLyOccArray !== null) {
        vl = realLyOccArray[x];
      } else {
        vl = Math.min(100, fL(x) * (1 + (rnd() - .5) * .05));
      }
      vl = Math.max(lastL, vl);
      lastL = vl;
      ly.push([x, vl]);

      if (x >= s.dba){
        let vt;
        if (realTyOccArray !== null) {
          vt = realTyOccArray[x];
        } else {
          vt = Math.min(100, fT(x) * (1 + (rnd() - .5) * .05));
        }
        vt = Math.max(lastT, vt);
        if (x === s.dba) vt = s.otb;
        lastT = vt;
        ty.push([x, vt]);
      }
      if (x <= s.dba) fc.push([x, fT(x)]);
    }
    const at = (arr, x) => (arr.find(pt => pt[0] === x) || [x, null])[1];
    const result = {ty, fc, ly, stlyFinal, at, actualStlyRooms, actualStlyOcc};
    if (a) a._bookingCurves = result;
    return result;
  }

  function planHistory(a, row, precomputedCurves){
    const rnd = seeded(a.s.d + planKey(row.p));
    const c = precomputedCurves || bookingCurves(a);

    // Index STLY reservations by plan code on `a` once for O(1) lookup:
    if (!a._stlyByPlan) {
      const map = {};
      const list = activeRes || [];
      for (let i = 0; i < list.length; i++) {
        const r = list[i];
        if (r.is_stly || r.isStly) {
          const code = r.rate_plan_code || r.plan;
          if (code) {
            if (!map[code]) map[code] = [];
            map[code].push(r);
          }
        }
      }
      a._stlyByPlan = map;
    }

    const planStlyRes = (a._stlyByPlan && a._stlyByPlan[row.p.code]) || [];
    const hasPlanStly = planStlyRes.length > 0;
    const realStlyRooms = hasPlanStly ? planStlyRes.reduce((sum, r) => sum + (Number(r.rooms || r.room_count) || 1), 0) : null;
    const realStlyRate = hasPlanStly ? Math.round(planStlyRes.reduce((sum, r) => sum + (Number(r.nightly_rate ?? r.sell ?? r.rate) || 0), 0) / planStlyRes.length) : null;

    const jitter = .75 + rnd() * .5, stlyShare = (row.p.share || 0.1) * jitter;
    const scale = (arr, share) => arr.map(([x, v]) => [x, v / 100 * activeCapacity * share]);
    const lead = (row.p.lead || [1, 2, 3, 4, 3, 1]).map(v => v);

    let leadLy;
    if (hasPlanStly) {
      const buckets = [0, 0, 0, 0, 0, 0];
      for (let i = 0; i < planStlyRes.length; i++) {
        const r = planStlyRes[i];
        const arr = toCalendarYMD(r.arrival_date || r.arrival);
        const bkd = toCalendarYMD(r.booking_date || r.booked);
        const ld = (arr && bkd) ? Math.max(0, diffDays(bkd, arr)) : 0;
        if (ld <= 3) buckets[0]++;
        else if (ld <= 7) buckets[1]++;
        else if (ld <= 14) buckets[2]++;
        else if (ld <= 30) buckets[3]++;
        else if (ld <= 60) buckets[4]++;
        else buckets[5]++;
      }
      const bCount = buckets.reduce((x, y) => x + y, 0) || 1;
      leadLy = buckets.map(v => Math.round((v / bCount) * 100));
    } else {
      leadLy = lead.map(v => Math.max(0, Math.round(v * (.8 + rnd() * .4))));
    }

    const n = lead.reduce((x,y)=>x+y,0) || 1, nl = leadLy.reduce((x,y)=>x+y,0) || 1;
    const mids = [1.5,5.5,11,22.5,45,75];
    const avgLead = lead.reduce((sum, v, k) => sum + v * mids[k], 0) / n;
    const avgLeadLy = leadLy.reduce((sum, v, k) => sum + v * mids[k], 0) / nl;
    const stlyRooms = realStlyRooms !== null ? realStlyRooms : Math.round(a.s.stly / 100 * activeCapacity * stlyShare);
    const stlyRate = realStlyRate !== null && realStlyRate > 0 ? realStlyRate : Math.round(row.sell * (a.s.stlyAdr || row.sell) / (a.s.adr || 1));
    const ty = scale(c.ty, row.p.share || 0.1), ly = scale(c.ly, stlyShare);
    const pick = (arr, x0, x1) => {
      const f = arr.find(p => p[0] === x0), g = arr.find(p => p[0] === x1);
      return f && g ? g[1] - f[1] : 0;
    };
    return {
      ty, ly,
      lead: lead.map(v => v / n * 100),
      leadLy: leadLy.map(v => v / nl * 100),
      avgLead, avgLeadLy, stlyRooms, stlyRate,
      pickup7: Math.round(pick(ty, a.s.dba + 7, a.s.dba)),
      pickup7ly: Math.round(pick(ly, a.s.dba + 7, a.s.dba))
    };
  }

  function lineChart({
    series,
    yMax,
    yLabel,
    marker,
    w = 620,
    h = 240,
    yFmt = v => Math.round(v),
    isOccupancy = (yLabel === 'Occupancy %'),
    capacity = activeCapacity,
    stayDate = null
  }){
    const L = 44, R = 12, T = 12, B = 30, muted = cssVar('--muted'), line = cssVar('--line');
    const X = x => L + (SHOW - x) / SHOW * (w - L - R), Y = v => T + (1 - v / yMax) * (h - T - B);
    let g = `<rect x="${L}" y="${T}" width="${w-L-R}" height="${h-T-B}" fill="none" stroke="${line}"/>`;
    for (let k = 0; k <= 4; k++){
      const v = yMax * k / 4;
      g += `<line x1="${L}" x2="${w-R}" y1="${Y(v)}" y2="${Y(v)}" stroke="${line}" opacity=".6"/><text x="${L-6}" y="${Y(v)+3}" text-anchor="end" font-size="10" fill="${muted}" font-family="JetBrains Mono, monospace">${yFmt(v)}</text>`;
    }
    const ticks = SHOW > 90 ? [SHOW, 90, 60, 30, 14, 7, 0] : [90, 60, 30, 14, 7, 0];
    ticks.forEach(x => g += `<text x="${X(x)}" y="${h-B+14}" text-anchor="middle" font-size="10" fill="${muted}" font-family="JetBrains Mono, monospace">${x}</text>`);
    g += `<text x="${(L+w-R)/2}" y="${h-3}" text-anchor="middle" font-size="10.5" fill="${muted}">Days before arrival</text>`;
    g += `<text transform="translate(11 ${(T+h-B)/2}) rotate(-90)" text-anchor="middle" font-size="10.5" fill="${muted}">${yLabel}</text>`;
    if (marker != null) {
      g += `<line x1="${X(marker)}" x2="${X(marker)}" y1="${T}" y2="${h-B}" stroke="${cssVar('--text')}" stroke-dasharray="2 3" opacity=".5"/><text x="${X(marker)+4}" y="${T+11}" font-size="10.5" fill="${cssVar('--text')}">Today</text>`;
    }
    series.forEach(sr => {
      if (!sr.pts.length) return;
      const d = sr.pts.map((p, k) => `${k?'L':'M'}${X(p[0]).toFixed(1)},${Y(Math.min(yMax,p[1])).toFixed(1)}`).join('');
      g += `<path d="${d}" fill="none" stroke="${sr.color}" stroke-width="${sr.width||2.2}" ${sr.dash?`stroke-dasharray="${sr.dash}"`:''} stroke-linejoin="round" stroke-linecap="round"/>`;
    });

    const chartConfig = {
      series: series.map(sr => ({
        pts: sr.pts || [],
        color: sr.color,
        width: sr.width || 2.2,
        dash: sr.dash || null,
        label: sr.label || '',
        key: sr.key || ''
      })),
      yMax,
      yLabel,
      marker,
      w,
      h,
      L,
      R,
      T,
      B,
      SHOW,
      isOccupancy: !!isOccupancy,
      capacity: capacity || 0,
      stayDate
    };
    const encoded = encodeURIComponent(JSON.stringify(chartConfig));

    return `<div class="interactive-chart-wrapper" data-chart-interactive="${encoded}">
      <svg class="chart" viewBox="0 0 ${w} ${h}" role="img" aria-label="${esc(yLabel)} by days before arrival">${g}</svg>
    </div>`;
  }

  function groupedBars({labels, a, b, colorA, colorB, w = 560, h = 190, yLabel}){
    const L = 36, R = 8, T = 10, B = 30, muted = cssVar('--muted'), line = cssVar('--line');
    const max = Math.max(...a, ...b, 1) * 1.15, bw = (w - L - R) / (labels.length || 1);
    const Y = v => T + (1 - v / max) * (h - T - B);
    let g = `<line x1="${L}" x2="${w-R}" y1="${h-B}" y2="${h-B}" stroke="${line}"/>`;
    labels.forEach((lab, k) => {
      const x = L + k * bw, cw = bw * .32;
      g += `<rect x="${x+bw*.16}" y="${Y(a[k] || 0)}" width="${cw}" height="${Math.max(0, h-B-Y(a[k] || 0))}" rx="3" fill="${colorA}"/>`;
      g += `<rect x="${x+bw*.16+cw+3}" y="${Y(b[k] || 0)}" width="${cw}" height="${Math.max(0, h-B-Y(b[k] || 0))}" rx="3" fill="${colorB}"/>`;
      g += `<text x="${x+bw/2}" y="${h-B+14}" text-anchor="middle" font-size="10" fill="${muted}" font-family="JetBrains Mono, monospace">${lab}</text>`;
    });
    g += `<text x="${(L+w-R)/2}" y="${h-2}" text-anchor="middle" font-size="10.5" fill="${muted}">${yLabel}</text>`;
    return `<svg class="chart" viewBox="0 0 ${w} ${h}" role="img" aria-label="${esc(yLabel)}">${g}</svg>`;
  }

  function analyseNbText(a){
    const i = a.i;
    return [-2,-1,0,1,2].map(o => {
      const j = i + o;
      return (j < 0 || j >= activeDates.length) ? '—' : fmt$(hurdleOf(sigs(j)));
    }).join(' / ');
  }

  function datePayload(a){
    const s = a.s;
    return {
      property_code: activeProperty,
      as_of_date: activeAsOf,
      stay_date: s.d,
      days_before_arrival: s.dba,
      signals: {
        otb_occ_pct: r2(s.otb),
        stly_otb_occ_pct: r2(s.stly),
        forecast_occ_pct: r2(s.fcst),
        adr: r2(s.adr),
        stly_adr: r2(s.stlyAdr),
        bar: s.bar,
        pace_index: r2(paceIndex(s)),
        adr_index: r2(adrIndex(s))
      },
      diagnosis: Q[a.q].id,
      lever_window: LEVERS[a.w]?.id || a.w,
      hurdle_net_rate: a.h,
      hurdle_pct_of_bar: tierOf(s).pct,
      bar_move: a.mv,
      new_bar: a.mv ? s.bar + a.mv : null,
      confidence: a.conf,
      attention_score: SCR.list[a.i]?.score || 0,
      rank: SCR.list[a.i]?.rank || 1,
      on_strip: SCR.list[a.i]?.onStrip || false,
      actions: a.rows.filter(r => r.actionable).map(r => planAction(a, r)),
      promotion: promoPayload(promoForDate(a))
    };
  }

  function planAction(a, r){
    return {
      rate_plan_code: r.p.code,
      channel: r.p.channel,
      plan_type: r.p.type,
      rule_id: r.rule,
      action: r.rule === 'R9a' ? 'SET_MIN_LOS' : r.rule === 'R3' ? (a.mv > 0 ? 'RAISE_RATE' : 'LOWER_RATE') : r.rule === 'R9' ? (a.w === 'closein' ? 'CLOSE_TO_ARRIVAL' : 'CLOSE') : r.rule === 'R6' ? 'REOPEN' : r.act.toUpperCase().replace(/ /g,'_'),
      current_sell_rate: r.sell,
      new_sell_rate: r.newRate,
      min_los: r.minLos || null,
      net_rate: r2(r.net),
      hurdle_net_rate: a.h,
      reason: r.reason
    };
  }

  const jsonHtml = obj => esc(JSON.stringify(obj, null, 2));

  const SEG = p => p.type === 'BAR' ? 'Transient retail' : p.type === 'Discount' ? 'Transient discount' : p.type === 'Package' ? 'Package' : p.type === 'Opaque' ? 'Opaque' : p.type === 'Group' ? 'Group' : p.code === 'GOV' ? 'Government' : 'Corporate negotiated';

  const addDays = (iso, n) => {
    const ymd = toCalendarYMD(iso);
    if (!ymd) return '';
    const m = moment(ymd, 'YYYY-MM-DD');
    return m.isValid() ? m.add(n, 'days').format('YYYY-MM-DD') : '';
  };

  const diffDays = (a, b) => {
    const ya = toCalendarYMD(a);
    const yb = toCalendarYMD(b);
    if (!ya || !yb) return 0;
    const ma = moment(ya, 'YYYY-MM-DD');
    const mb = moment(yb, 'YYYY-MM-DD');
    if (!ma.isValid() || !mb.isValid()) return 0;
    return Math.round(mb.diff(ma, 'days', true));
  };

  function reservations(a){
    const ck = a.s.d + JSON.stringify(overrides[a.i] || overrides[a.s.d] || {});
    if (resCache[ck]) return resCache[ck];
    const matchingRes = (activeRes || []).filter(x => {
      if (x.is_stly || x.isStly) return false;
      const arr = toCalendarYMD(x.arrival_date || x.arrival);
      const dep = toCalendarYMD(x.departure_date || x.depart);
      if (!arr) return false;
      return arr <= a.s.d && (dep ? a.s.d < dep : a.s.d === arr);
    });

    if (matchingRes.length > 0){
      const byKey = Object.fromEntries(a.rows.map(r => [planKey(r.p), r]));
      const byCode = Object.fromEntries(a.rows.map(r => [r.p.code, r]));
      const list = matchingRes.map(x => {
        const channel = normalizeChannelName(x.channel);
        const planCode = x.rate_plan_code || x.plan || 'BAR';
        const r = (x.channel ? byKey[planCode + '|' + x.channel] || byKey[planCode + '|' + channel] : null) || byCode[planCode] || a.rows[0] || null;
        const p = r?.p || { code: planCode, channel, comm: 0, cost: 0, type: 'Discount' };
        const sell = r ? r.sell : (x.nightly_rate ?? x.sell ?? 0);
        const rawComm = x.commission != null ? (Number(x.commission) > 1 ? Number(x.commission) / 100 : Number(x.commission)) : (p.comm || 0);
        const rate = Number(x.nightly_rate ?? x.sell ?? 0);
        const net = x.net_rate != null ? Number(x.net_rate) : r2(rate * (1 - rawComm) - (p.cost || 0));
        const isFixed = x.is_fixed === true || x.is_fixed === 'Y' || x.is_fixed === '1' || x.is_fixed === 'true';
        const arrival = toCalendarYMD(x.arrival_date || x.arrival);
        const depart = toCalendarYMD(x.departure_date || x.depart);
        const booked = toCalendarYMD(x.booking_date || x.booked);
        const nights = Math.max(1, diffDays(arrival, depart) || Number(x.nights) || 1);
        const lead = Math.max(0, diffDays(booked, arrival) || Number(x.lead) || 0);
        const o = {
          conf: x.confirmation_no || x.conf || '',
          guest: x.guest_name || x.guest || 'Guest',
          booked,
          arrival,
          depart,
          nights,
          lead,
          room: x.room_type || x.room || 'Room',
          plan: planCode,
          planKey: planCode + '|' + channel,
          channel,
          segment: x.market_segment || SEG(p),
          fixed: isFixed,
          comm: rawComm,
          cost: p.cost || 0,
          rate,
          net,
          todaySell: sell,
          status: x.status || 'Confirmed',
          cancelDate: x.cancel_date || x.cancelDate || null
        };
        o.vsToday = o.rate - o.todaySell;
        return o;
      });
      return (resCache[ck] = list);
    }

    return (resCache[ck] = []);
  }

  function channelGaps(a){
    const safeI = (a.i >= 0 && a.i < activeDates.length) ? a.i : 0;
    const ch = activeDates[safeI]?.ch || {};
    const allKeys = new Set([...CHANNELS, ...Object.keys(ch).filter(k => !['Brand.com', 'Booking.com', 'Expedia'].includes(k))]);
    const list = Array.from(allKeys).sort((a, b) => {
      if (a === 'BRAND' || a === 'Brand.com') return -1;
      if (b === 'BRAND' || b === 'Brand.com') return 1;
      return a.localeCompare(b);
    });
    return list.map(k => {
      const pair = ch[k] || (k === 'BRAND' ? ch['Brand.com'] : null) || [0, 0];
      const ty = Number(pair[0]) || 0;
      const ly = Number(pair[1]) || 0;
      return { ch: k, ty, ly, delta: ty - ly };
    });
  }

  function promoForDate(a){
    const s = a.s, q = a.q, gaps = channelGaps(a), why = [];
    const base = {date:s.d, dba:s.dba, quadrant:q, gaps, hurdle:a.h};
    if (q === 'under' || (q === 'healthy' && s.fcst >= CONFIG.healthyTightenMinFcst) || s.fcst >= CONFIG.anyTightenMinFcst)
      return Object.assign(base, {kind:'BLACKOUT', rule:'P1', title:'Protect rate, no promotion', detail:`Forecast ${P1(s.fcst)} and ${Q[q]?.label?.toLowerCase() || ''}. Exclude this date from running OTA and brand promotions and pause member and mobile discounts.`});
    if (q === 'healthy') return Object.assign(base, {kind:'NONE', rule:'P2', title:'No promotion needed', detail:'Pacing ahead of last year at a healthy rate. Keep current promotions as they are.'});
    const worst = gaps.slice().sort((m, n) => m.delta - n.delta)[0];
    const owner = worst && worst.delta < 0 ? worst.ch : (CHANNELS[0] || 'Brand.com');
    why.push(worst && worst.delta < 0 ? `${owner} is ${commonService.formateRoom(-worst.delta)} room${worst.delta < -1 ? 's' : ''} behind STLY, the largest channel gap` : 'No OTA is behind STLY, so the brand channel leads');
    const ownerConfig = PROMO_OWNERS[owner] || { cls: 'o-channel', comm: 0.18 };
    const comm = ownerConfig.comm ?? 0.18;
    const gapRooms = Math.max(0, Math.round(((s.stly || 0) / 100) * activeCapacity) - Math.round(((s.otb || 0) / 100) * activeCapacity));
    const effectiveBar = (s.bar > 0) ? s.bar : ((s.adr > 0) ? s.adr : 0);
    const netBar = effectiveBar * (1 - comm);
    const discMax = netBar > 0 ? Math.max(0, 1 - (a.h || 0) / netBar) : 0;
    let type, desired = 0, disc = 0;
    if (q === 'resist'){
      type = 'VALUE_ADD';
      why.push(`ADR is ${commonService.formateAmount(s.adr - s.stlyAdr)} ahead of STLY, so add value instead of cutting rate`);
    } else {
      desired = paceIndex(s) < .85 ? .15 : .10;
      disc = Number.isFinite(discMax) ? Math.floor((Math.min(desired, discMax) + 1e-6) * 20) / 20 : 0;
      if (isNaN(disc) || !Number.isFinite(disc) || disc < 0) disc = 0;
      type = s.dba <= 7 ? 'LAST_MINUTE' : s.dba <= 45 ? 'MEMBER' : 'EARLY_BOOKER';
      why.push(`${commonService.formateRoom(s.dba)} days out, so ${type === 'EARLY_BOOKER' ? 'an' : 'a'} ${PROMO_TYPE_LABEL[type]?.toLowerCase() || 'offer'} fits the booking window`);
      if (disc < .05){
        why.push(`Hurdle ${commonService.formateAmount(a.h)} leaves room for only ${Math.round((Number.isFinite(discMax) ? discMax : 0) * 100)}% off, so switch to value-add`);
        type = 'VALUE_ADD';
        disc = 0;
      } else if (disc < desired) {
        why.push(`Discount trimmed from ${Math.round(desired * 100)}% to ${Math.round(disc * 100)}% to stay above the hurdle`);
      }
    }
    const net = effectiveBar > 0 ? Math.round(effectiveBar * (1 - (disc || 0)) * (1 - comm)) : (a.h || 0);
    why.push(`Pace ${P1(s.otb)} vs. ${P1(s.stly)} STLY: ${commonService.formateRoom(gapRooms)} room${gapRooms === 1 ? '' : 's'} to recover`);
    const end = type === 'EARLY_BOOKER' ? addDays(s.d, -30) : s.d;
    const promoName = PROMO_NAMES[owner]?.[type] || `${owner} ${PROMO_TYPE_LABEL[type] || 'Promotion'}`;
    const res = Object.assign(base, {
      kind: 'PROMO',
      rule: q === 'resist' || type === 'VALUE_ADD' ? 'P4' : 'P5',
      owner, type, name: promoName,
      disc: disc || 0, desired,
      discMax: Number.isFinite(discMax) ? discMax : 0,
      net: Number.isFinite(net) ? net : 0,
      fence: PROMO_FENCES[type] || '',
      window: { start: activeAsOf, end },
      targetRooms: gapRooms,
      why
    });
    if (s.ev && q === 'demand') {
      const brandEventName = PROMO_NAMES['BRAND']?.EVENT || PROMO_NAMES['Brand.com']?.EVENT || 'Event package';
      const evList = String(s.ev).split(',').map(x => x.trim()).filter(Boolean);
      const evTitle = evList.length > 2
        ? `${evList[0]} & ${evList.length - 1} more events ${brandEventName.toLowerCase()}`
        : `${s.ev} ${brandEventName.toLowerCase()}`;
      res.secondary = {owner: owner === 'BRAND' ? 'BRAND' : 'Brand.com', type:'EVENT', name: evTitle, events: s.ev, fence:PROMO_FENCES.EVENT, rule:'P7'};
    }
    return res;
  }

  function promoPayload(p){
    if (p.kind !== 'PROMO') return {recommendation:p.kind, rule_id:p.rule};
    return {
      recommendation: 'PROMOTION',
      rule_id: p.rule,
      owner: p.owner,
      promo_type: p.type,
      name: p.name,
      discount_pct: Math.round(p.disc * 100),
      value_add: p.type === 'VALUE_ADD',
      fence: p.fence,
      booking_window: {start:p.window.start, end:p.window.end},
      target_rooms: p.targetRooms,
      promo_net_rate: p.net,
      hurdle_net_rate: p.hurdle,
      secondary: p.secondary ? {owner:p.secondary.owner, promo_type:p.secondary.type, name:p.secondary.name, events:p.secondary.events} : null
    };
  }

  function buildCampaigns(){
    const list = activeDates.map((_, i) => { const a = analyse(i); return {a, p:promoForDate(a)}; }), groups = {};
    const add = (key, owner, type, name, fence, a, p, sec, ev) => {
      const g = groups[key] = groups[key] || {owner, type, name, fence, items:[], events: []};
      g.items.push({a, p, sec, ev});
      if (ev) g.events.push(ev);
    };
    list.forEach(({a, p}) => {
      if (p.kind !== 'PROMO') return;
      add(p.owner + '|' + p.type, p.owner, p.type, p.name, p.fence, a, p, false, a.s.ev);
      if (p.secondary) add((p.owner === 'BRAND' ? 'BRAND' : 'Brand.com') + '|EVENT|' + a.s.d, (p.owner === 'BRAND' ? 'BRAND' : 'Brand.com'), 'EVENT', p.secondary.name, p.secondary.fence, a, p, true, p.secondary.events || a.s.ev);
    });
    const blackouts = list.filter(o => o.p.kind === 'BLACKOUT').map(o => o.a.s.d);
    return Object.values(groups).map(g => {
      const dates = g.items.map(it => it.a.s.d).sort(), lo = dates[0], hi = dates[dates.length - 1];
      const discs = g.items.filter(it => !it.sec).map(it => Number.isFinite(it.p.disc) ? it.p.disc : 0);
      const disc = g.type === 'EVENT' || g.type === 'VALUE_ADD' ? 0 : (discs.length ? Math.min(...discs) : 0);
      const margins = g.items.map(it => {
        const pNet = Number.isFinite(it.p?.net) ? it.p.net : 0;
        const pHurdle = Number.isFinite(it.p?.hurdle) ? it.p.hurdle : 0;
        return pNet - pHurdle;
      });
      const margin = margins.length ? Math.min(...margins) : 0;
      const why = [...new Set(g.items.flatMap(it => {
        if (it.sec) {
          const evList = String(it.a.s.ev || '').split(',').map(x => x.trim()).filter(Boolean);
          const evLabel = evList.length > 2
            ? `${evList[0]} & ${evList.length - 1} more events`
            : it.a.s.ev;
          return [`${evLabel} is pacing ${P1(it.a.s.otb)} vs. ${P1(it.a.s.stly)} STLY`];
        }
        return (it.p?.why || []).slice(0, 2);
      }))].slice(0, 4);
      const endBook = g.items.map(it => it.p?.window?.end).filter(Boolean).sort().pop();
      const dateEvents = {};
      g.items.forEach(it => {
        if (it.a.s.ev) {
          dateEvents[it.a.s.d] = it.a.s.ev;
        }
      });
      const allEvents = Array.from(new Set(g.events.filter(Boolean))).join(', ');
      return {
        owner: g.owner,
        type: g.type,
        name: g.name,
        fence: g.fence,
        dates,
        disc,
        targetRooms: g.items.filter(it => !it.sec).reduce((t, it) => t + (Number.isFinite(it.p?.targetRooms) ? it.p.targetRooms : 0), 0) || g.items[0]?.p?.targetRooms || 0,
        bookStart: activeAsOf,
        bookEnd: g.type === 'EVENT' ? hi : (endBook || hi),
        blackout: blackouts.filter(d => d > lo && d < hi),
        margin,
        why,
        quads: [...new Set(g.items.map(it => Q[it.a.q]?.label || ''))].filter(Boolean),
        events: allEvents,
        dateEvents
      };
    }).sort((m, n) => n.targetRooms - m.targetRooms);
  }

  function rankRecord(x){
    return {
      rank: x.rank,
      stayDate: x.a.s.d,
      event: x.a.s.ev,
      daysOut: x.a.s.dba,
      diagnosis: x.a.q,
      score: x.score,
      actions: x.actions,
      revenueAtStake: x.stake,
      whyFlagged: why(x),
      index: x.i,
      onStrip: x.onStrip
    };
  }

  function renderPace(a){
    const c = bookingCurves(a), s = a.s, gold = cssVar('--gold'), muted = cssVar('--muted');
    const p7 = Math.round((s.otb - (c.at(c.ty, s.dba + 7) ?? s.otb)) / 100 * activeCapacity);
    const p7ly = Math.round(((c.at(c.ly, s.dba) ?? 0) - (c.at(c.ly, s.dba + 7) ?? 0)) / 100 * activeCapacity);
    const remaining = Math.round((s.fcst - s.otb) / 100 * activeCapacity), stlyRemaining = Math.round((c.stlyFinal - s.stly) / 100 * activeCapacity);
    const stlyFinal = Number(c.stlyFinal || 0).toFixed(2);
    return `
      <div class="section"><h3>Booking curve</h3><p class="hint">Occupancy on the books by days before arrival. STLY is aligned 364 days back so day of week matches.</p>
        <div class="legend"><span><i style="background:${gold}"></i>This year on the books</span><span><i class="dash"></i>Forecast to arrival</span><span><i style="background:${muted}"></i>STLY</span></div>
        ${lineChart({
          series:[
            {pts:c.ly, color:muted, width:1.8, label:'STLY', key:'ly'},
            {pts:c.ty, color:gold, width:2.6, label:'This year on the books', key:'ty'},
            {pts:c.fc, color:gold, dash:'5 4', label:'Forecast to arrival', key:'fc'}
          ],
          yMax: 100,
          yLabel: 'Occupancy %',
          marker: s.dba,
          yFmt: v => Math.round(v) + '%',
          isOccupancy: true,
          capacity: activeCapacity,
          stayDate: s.d
        })}</div>
      <div class="section"><div class="kpis">
        <div class="kpi"><div class="k">Pace index</div><div class="v num">${r2(paceIndex(s)).toFixed(2)}</div><div class="d">${P1(s.otb)} ÷ ${P1(s.stly)}</div></div>
        <div class="kpi"><div class="k">Pickup, last 7 days</div><div class="v num">${signedRoom(p7)} rooms</div><div class="d">STLY ${signedRoom(p7ly)} rooms</div></div>
        <div class="kpi"><div class="k">Still to pick up</div><div class="v num">${commonService.formateRoom(remaining)} rooms</div><div class="d">STLY picked up ${commonService.formateRoom(stlyRemaining)} from here</div></div>
        <div class="kpi"><div class="k">STLY final</div><div class="v num">${stlyFinal}%</div><div class="d">Forecast ${P1(s.fcst)}</div></div>
      </div></div>`;
  }

  function renderMix(a){
    const gold = cssVar('--gold'), muted = cssVar('--muted');
    const c = bookingCurves(a);
    const hist = a.rows.map(r => ({r, h:planHistory(a, r, c)}));
    const totRev = hist.reduce((x, o) => x + o.r.rooms * o.r.sell, 0) || 1;
    const maxRooms = Math.max(...hist.map(o => Math.max(o.r.rooms, o.h.stlyRooms)), 1);
    const chan = {};
    hist.forEach(({r, h}) => { const k = r.p.channel; chan[k] = chan[k] || {ty:0, ly:0}; chan[k].ty += r.rooms; chan[k].ly += h.stlyRooms; });
    const chanKeys = Object.keys(chan);
    return `
      <div class="section"><h3>Rooms by channel</h3><p class="hint">On the books today vs. STLY at the same days out.</p>
        <div class="legend"><span><i style="background:${gold}"></i>This year</span><span><i style="background:${muted}"></i>STLY</span></div>
        ${groupedBars({labels:chanKeys.map(k => k.replace('BRAND + GDS','Brand+GDS').replace('Brand.com + GDS','Brand+GDS').replace('All channels','All')), a:chanKeys.map(k=>chan[k].ty), b:chanKeys.map(k=>chan[k].ly), colorA:gold, colorB:muted, yLabel:'Rooms on the books'})}</div>
      <div class="tablewrap"><table>
        <thead><tr><th>Rate plan</th><th>Channel</th><th>Rooms vs. STLY</th><th class="r">Δ rooms</th><th class="r">Rate</th><th class="r">STLY rate</th><th class="r">Δ rate</th><th class="r">Revenue share</th><th></th></tr></thead>
        <tbody>${hist.map(({r, h}) => { const dr = r.rooms - h.stlyRooms, dp = r.sell - h.stlyRate;
          return `<tr class="clickable" data-row="${r.key}"><td><span class="code">${r.p.code}</span></td><td>${r.p.channel}</td>
            <td><span class="mini" style="width:${r.rooms/maxRooms*90}px;background:${gold}"></span><br><span class="mini" style="width:${h.stlyRooms/maxRooms*90}px;background:${muted};margin-top:3px"></span></td>
            <td class="r num ${dr>0?'delta-up':dr<0?'delta-down':''}">${signedRoom(dr)}</td><td class="r num">${fmt$(r.sell)}</td><td class="r num">${fmt$(h.stlyRate)}</td>
            <td class="r num ${dp>=0?'delta-up':'delta-down'}">${dp>=0?'+':'−'}${fmt$(Math.abs(dp))}</td><td class="r num">${(r.rooms*r.sell/totRev*100).toFixed(1)}%</td>
            <td><button class="drill" type="button">Details</button></td></tr>`; }).join('')}</tbody></table></div>`;
  }

  function renderTrace(a){
    const x = SCR.list[a.i] || { sPace:0, sAdr:0, sComp:0, sRev:0, sUrg:0, sEv:0, score:0, rank:1 };
    a.nbH = a.rows.length ? analyseNbText(a) : '';
    const s = a.s, t = tierOf(s), c = CONFIG.barRaise, cap = Math.round(s.bar * CONFIG.maxBarChangePct);
    const raw = a.q === 'under' ? (s.stlyAdr - s.adr) * c.adrGapWeight + (s.fcst - c.fcstPivot) * c.fcstWeight : null;
    const steps = [
      ['Pace index', `OTB ÷ STLY OTB at same days out = ${P1(s.otb)} ÷ ${P1(s.stly)}`, r2(paceIndex(s)).toFixed(2) + (paceIndex(s) >= CONFIG.paceAheadAt ? ' (ahead)' : ' (behind)')],
      ['ADR index', `ADR ÷ STLY ADR = ${fmt$(s.adr)} ÷ ${fmt$(s.stlyAdr)}`, r2(adrIndex(s)).toFixed(2) + (adrIndex(s) >= CONFIG.adrAheadAt ? ' (ahead)' : ' (behind)')],
      ['Diagnosis', 'Pace ahead/behind × ADR ahead/behind', Q[a.q].label, true],
      ['Lever window', `${commonService.formateRoom(s.dba)} days before arrival`, LEVERS[a.w]?.id || a.w],
      ['Hurdle net rate', `Forecast ${P1(s.fcst)} → tier ${Math.round(t.pct*100)}% of BAR ${fmt$(s.bar)}`, fmt$(a.h), true],
      ['Tighten?', 'Underpriced, or healthy and forecast ≥ 90%, or any date with forecast ≥ 95%', a.tighten ? 'Yes' : 'No'],
      ['BAR move', a.q === 'under' ? `max(${c.min}, (${s.stlyAdr} − ${s.adr}) × ${c.adrGapWeight} + (${s.fcst} − ${c.fcstPivot}) × ${c.fcstWeight}) = ${raw ? raw.toFixed(1) : '0'}, cap 12% = ${fmt$(cap)}`
        : a.q === 'healthy' ? `Forecast ${P1(s.fcst)} ${s.fcst >= CONFIG.healthyRaise.minFcst ? '≥' : '<'} ${CONFIG.healthyRaise.minFcst}% → ${s.fcst >= CONFIG.healthyRaise.minFcst ? '4% of BAR' : 'no move'}`
        : a.q === 'resist' ? `−max(${CONFIG.barLower.min}, (${s.adr} − ${s.stlyAdr}) × ${CONFIG.barLower.adrGapWeight}), cap 6% of BAR` : "Demand gap: hold BAR, don't chase rate down",
        a.mv === 0 ? 'Hold' : (a.mv > 0 ? '+' : '') + fmt$(a.mv), a.mv !== 0],
      ['Plan evaluation', `Each plan runs rules R1–R10 against hurdle ${fmt$(a.h)}`, `${commonService.formateRoom(a.rows.filter(r=>r.actionable).length)} actions`],
      ['Length-of-stay check (R9a)', `Before closing, test 2–3 night stays (net ≥ 85% of hurdle). Hurdles D-2…D+2: ${a.nbH}`, `${commonService.formateRoom(a.rows.filter(r=>r.rule==='R9a').length)} MinLOS, ${commonService.formateRoom(a.rows.filter(r=>r.rule==='R9').length)} close`],
      ['Confidence', `Medium if > 90 days out or pace within ±3% of STLY`, a.conf === 'HIGH' ? 'High' : 'Medium'],
      ['Attention score', `30×${x.sPace.toFixed(2)} pace + 20×${x.sAdr.toFixed(2)} ADR + 15×${x.sComp.toFixed(2)} compression + 20×${x.sRev.toFixed(2)} revenue + 15×${x.sUrg} urgency + 10×${x.sEv} event${x.actions ? '' : ', × 0.5 no action'}`, `${commonService.formateRoom(x.score)} (rank ${commonService.formateRoom(x.rank)})`, true]
    ];
    return `
      <div class="section"><h3>How this date was decided</h3><p class="hint">Highlighted steps drive the actions. Open any rate plan to see its own rule path.</p>
        <ol class="trace">${steps.map(([q, f, v, hit]) => `<li class="${hit?'hit':''}"><div><div class="q">${q}</div><div class="f">${esc(f)}</div></div><div class="val num">${esc(v)}</div></li>`).join('')}</ol></div>
      <div class="section"><div class="sec-head"><div><h3>Recommendation payload</h3><p class="hint">Proposed API response for this stay date.</p></div><button class="copy" type="button" data-copy="date">Copy JSON</button></div>
        <pre class="json" id="dateJson">${jsonHtml(datePayload(a))}</pre></div>`;
  }

  function renderSpec(){
    const inputs = [
      ['stay_date','PMS reservations','Stay date','ISO date'],
      ['as_of_date','System','Snapshot','Daily run date'],
      ['days_before_arrival','Derived','Stay date','stay_date − as_of_date'],
      ['otb_rooms / otb_occ_pct','PMS reservations','Stay date','Rooms on the books net of cancellations at as-of date'],
      ['stly_otb_occ_pct','Reservation history','Stay date','OTB at stay_date − 364 as of as_of_date − 364 (needs booking and cancel timestamps)'],
      ['forecast_occ_pct','Forecast Engine','Stay date','Unconstrained final occupancy forecast'],
      ['adr / stly_adr','PMS reservations','Stay date','Room revenue ÷ rooms, same snapshot logic as OTB'],
      ['bar','Channel manager / RMS','Stay date','Current lead BAR on BRAND'],
      ['rate_plan_code, channel','Channel manager mapping','Plan × channel','One row per sellable plan per channel'],
      ['plan_type','Rate code classification','Plan','BAR, Discount, Package, Opaque, Negotiated, Group, Loyalty'],
      ['derived_from_bar, discount_pct, add_on','Rate plan config','Plan','How sell rate is computed from BAR'],
      ['fixed_rate','Contract','Plan','Negotiated, government, group'],
      ['commission_pct, package_cost','Finance config','Plan × channel','Used for net rate'],
      ['min_booking_window','Rate plan config','Plan','e.g. 14 for ADV14'],
      ['is_closed','Channel manager','Plan × channel × date','Current open/closed status'],
      ['rooms_by_plan, lead_time_by_plan','PMS reservations','Plan × stay date','For plan mix and drill-down history']
    ];
    const cfgRows = [
      ['capacity', `${activeCapacity} rooms`, 'Total property rooms (from property detail config)'],
      ['paceAheadAt', CONFIG.paceAheadAt, 'Pace index at or above this is ahead'],
      ['adrAheadAt', CONFIG.adrAheadAt, 'ADR index at or above this is ahead'],
      ['windows', `>${CONFIG.windows.priceOnlyAbove} price, >${CONFIG.windows.deepAbove} deep, >${CONFIG.windows.closuresAbove} closures, else close-in`, 'Days before arrival'],
      ['hurdleTiers', CONFIG.hurdleTiers.map(t => `≥${t.minFcst}%: ${Math.round(t.pct*100)}%`).join(', '), 'Hurdle = BAR × tier % by forecast'],
      ['deepWindowCloseMinFcst', CONFIG.deepWindowCloseMinFcst + '%', 'Closures allowed 31–90 days only above this forecast'],
      ['healthyTightenMinFcst / anyTightenMinFcst', `${CONFIG.healthyTightenMinFcst}% / ${CONFIG.anyTightenMinFcst}%`, 'When healthy or any date tightens'],
      ['barRaise', `max(${CONFIG.barRaise.min}, gap × ${CONFIG.barRaise.adrGapWeight} + (fcst − ${CONFIG.barRaise.fcstPivot}) × ${CONFIG.barRaise.fcstWeight})`, 'Underpriced BAR move'],
      ['healthyRaise', `${CONFIG.healthyRaise.pct*100}% of BAR if forecast ≥ ${CONFIG.healthyRaise.minFcst}%`, 'Healthy BAR move'],
      ['barLower', `max(${CONFIG.barLower.min}, gap × ${CONFIG.barLower.adrGapWeight}), cap ${CONFIG.barLower.maxPct*100}%`, 'Price resistance BAR move'],
      ['maxBarChangePct', CONFIG.maxBarChangePct*100 + '%', 'Daily guardrail on any BAR move'],
      ['los', `max MinLOS ${CONFIG.los.maxMinLos}, net ≥ ${CONFIG.los.minNetPctOfHurdle*100}% of hurdle, enabled ${CONFIG.los.enabled}`, 'R9a length-of-stay check'],
      ['screener', `top ${CONFIG.screener.topN}; weights pace ${CONFIG.screener.wPace}, ADR ${CONFIG.screener.wAdr}, compression ${CONFIG.screener.wComp}, revenue ${CONFIG.screener.wDollar}, urgency ${CONFIG.screener.wUrg}, event ${CONFIG.screener.wEvent}; no-action × ${CONFIG.screener.noActionFactor}`, 'Attention score and strip selection'],
      ['lowConfidence', `> ${CONFIG.lowConfidence.dbaAbove} days, pace within ±${CONFIG.lowConfidence.paceBand*100}%, < ${CONFIG.lowConfidence.minHistoryMonths} months history`, 'Flags Medium/Low confidence']
    ];
    return `<div class="spec">
      <div class="section"><h3>Formulas</h3><p class="hint">Phase 1 is deterministic. Every output can be explained from these lines.</p>
        <table><tbody>
          <tr><td>Pace index</td><td><code>otb_occ_pct ÷ stly_otb_occ_pct</code></td></tr>
          <tr><td>ADR index</td><td><code>adr ÷ stly_adr</code></td></tr>
          <tr><td>Diagnosis</td><td><code>pace ≥ 1 &amp; adr ≥ 1 → HEALTHY · pace ≥ 1 &amp; adr &lt; 1 → UNDERPRICED · pace &lt; 1 &amp; adr ≥ 1 → PRICE_RESISTANCE · else DEMAND_GAP</code></td></tr>
          <tr><td>Sell rate</td><td><code>fixed_rate ?? round(bar × (1 − discount_pct) + add_on)</code></td></tr>
          <tr><td>Net rate</td><td><code>sell × (1 − commission_pct) − package_cost</code></td></tr>
          <tr><td>Hurdle</td><td><code>round(bar × tier_pct(forecast_occ_pct))</code></td></tr>
          <tr><td>Tighten</td><td><code>UNDERPRICED or (HEALTHY &amp; fcst ≥ 90) or fcst ≥ 95</code></td></tr>
          <tr><td>LOS margin (R9a)</td><td><code>Σ (net_d − hurdle_d) over a 2–3 night window containing D ≥ 0, and net_D ≥ 0.85 × hurdle_D</code></td></tr>
          <tr><td>Attention score</td><td><code>(30·pace + 20·adr + 15·comp + 20·revenue + 15·urgency + 10·event) × (0.5 if no action)</code></td></tr>
        </tbody></table></div>
      <div class="section"><h3>Rule catalogue</h3><p class="hint">Evaluated in order per rate plan; first match wins.</p>
        <div class="tablewrap"><table><thead><tr><th>ID</th><th>Name</th><th>Condition</th><th>Output</th></tr></thead>
        <tbody>${RULES.map(r => `<tr><td class="num">${r[0]}</td><td><code>${r[1]}</code></td><td>${r[2]}</td><td>${r[3]}</td></tr>`).join('')}</tbody></table></div></div>
      <div class="section"><h3>Configuration</h3><p class="hint">Property-level settings. Values shown are defaults used in this prototype.</p>
        <div class="tablewrap"><table><thead><tr><th>Key</th><th>Default</th><th>Meaning</th></tr></thead>
        <tbody>${cfgRows.map(r => `<tr><td><code>${r[0]}</code></td><td>${esc(r[1])}</td><td>${r[2]}</td></tr>`).join('')}</tbody></table></div></div>
      <div class="section"><h3>Input data contract</h3><p class="hint">What the engine needs per run.</p>
        <div class="tablewrap"><table><thead><tr><th>Field</th><th>Source</th><th>Grain</th><th>Notes</th></tr></thead>
        <tbody>${inputs.map(r => `<tr><td><code>${r[0]}</code></td><td>${r[1]}</td><td>${r[2]}</td><td>${r[3]}</td></tr>`).join('')}</tbody></table></div></div>
      <div class="section"><h3>Promotion rules</h3><p class="hint">Evaluated per stay date, then grouped into campaigns.</p>
        <div class="tablewrap"><table><thead><tr><th>ID</th><th>Name</th><th>Condition</th><th>Output</th></tr></thead>
        <tbody>${PROMO_RULES.map(r => `<tr><td class="num">${r[0]}</td><td><code>${r[1]}</code></td><td>${r[2]}</td><td>${r[3]}</td></tr>`).join('')}</tbody></table></div></div>
      <div class="section"><h3>Reservation fields</h3><p class="hint">Needed for the reservation drill-down, STLY snapshots and lead-time mix.</p>
        <div class="tablewrap"><table><thead><tr><th>Field</th><th>Notes</th></tr></thead><tbody>
          ${RES_FIELDS.map(r => `<tr><td><code>${r[0]}</code></td><td>${r[1]}</td></tr>`).join('')}</tbody></table></div></div>
      <div class="section"><h3>Guardrails and outcome tracking</h3>
        <table><tbody>
          <tr><td>Hysteresis</td><td>Do not reverse a plan's open/closed state within 48 hours unless forecast moves ≥ 5 pts.</td></tr>
          <tr><td>Minimum volume</td><td>Skip plan-level closures when date OTB &lt; 5 rooms.</td></tr>
          <tr><td>Rate floor and ceiling</td><td>Property config; BAR moves are clamped before output.</td></tr>
          <tr><td>Cold start</td><td>Under 6 months of history: confidence LOW, recommendations shown but not auto-pushed.</td></tr>
          <tr><td>Audit log</td><td>Store payload, user decision (approved, edited, dismissed) and actual final occupancy and ADR per date for regret analysis.</td></tr>
        </tbody></table></div>
    </div>`;
  }

  function renderPromoTab(a){
    const p = promoForDate(a), maxR = Math.max(...p.gaps.map(g => Math.max(g.ty, g.ly)), 1), gold = cssVar('--gold'), muted = cssVar('--muted');
    const hero = p.kind === 'PROMO'
      ? `<div class="promo-hero ${PROMO_OWNERS[p.owner]?.cls || 'o-channel'}"><span class="owner">${p.owner}</span> <span class="pname">${PROMO_TYPE_LABEL[p.type] || ''}</span>
          <h3>${esc(p.name)}</h3><p>${p.disc ? `${Math.round(p.disc * 100)}% off BAR` : 'Value-add, no rate cut'}. ${p.fence}. Book ${md(p.window.start)} to ${md(p.window.end)}.</p>
          ${p.secondary ? `<p style="margin-top:8px"><b>Also:</b> BRAND ${esc(p.secondary.name)} (${p.secondary.fence.toLowerCase()}).</p>` : ''}</div>`
      : `<div class="promo-hero"><span class="badge ${p.kind === 'BLACKOUT' ? 'b-close' : 'b-keep'}">${p.kind === 'BLACKOUT' ? 'Blackout' : 'No promotion'}</span><h3>${p.title}</h3><p>${p.detail}</p></div>`;
    const steps = p.kind === 'PROMO' ? [
      ['Diagnosis', Q[a.q]?.label || '', `${Q[a.q]?.label || ''} dates can take a promotion`],
      ['Owner channel', p.owner, 'Largest room shortfall vs. STLY (P3)'],
      ['Promotion type', PROMO_TYPE_LABEL[p.type] || '', a.q === 'resist' ? 'Price resistance → value-add (P4)' : `${commonService.formateRoom(a.s.dba)} days out (P5)`],
      ['Desired discount', p.desired ? Math.round(p.desired * 100) + '%' : '—', 'Pace index < 0.85 → 15%, else 10%'],
      ['Max discount allowed', Math.round(p.discMax * 100) + '%', `1 − hurdle ${fmt$(a.h)} ÷ (BAR ${fmt$(a.s.bar)} × (1 − commission ${Math.round((PROMO_OWNERS[p.owner]?.comm || 0.18) * 100)}%))`],
      ['Promo net rate', fmt$(p.net), `vs. hurdle ${fmt$(a.h)} (P6)`]
    ] : [['Rule', p.rule, p.kind === 'BLACKOUT' ? 'Compressed date: protect rate (P1)' : 'Healthy below 90% forecast (P2)']];
    return `
      <div class="section"><h3>Recommended promotion for ${md(a.s.d)}</h3><p class="hint">Campaigns across all dates are planned below the main panels.</p>${hero}</div>
      <div class="section"><h3>Rooms by channel vs. STLY</h3><p class="hint">The channel furthest behind owns the campaign.</p>
        <div class="legend"><span><i style="background:${gold}"></i>This year</span><span><i style="background:${muted}"></i>STLY</span></div>
        <div class="gapbars">${p.gaps.map(g => `<div>${g.ch}</div><div><div class="tr"><span style="width:${g.ty / maxR * 100}%;background:${gold}"></span></div><div class="tr" style="margin-top:4px"><span style="width:${g.ly / maxR * 100}%;background:${muted}"></span></div></div>
          <div class="num ${g.delta >= 0 ? 'delta-up' : 'delta-down'}" style="text-align:right">${g.delta >= 0 ? '+' : ''}${commonService.formateRoom(g.delta)} rooms</div>`).join('')}</div></div>
      <div class="section"><h3>How it was decided</h3>
        <ol class="trace">${steps.map(([q, v, f]) => `<li class="hit"><div><div class="q">${q}</div><div class="f">${esc(f)}</div></div><div class="val num">${esc(v)}</div></li>`).join('')}</ol></div>
      <div class="section"><div class="sec-head"><div><h3>Promotion payload</h3><p class="hint">Proposed API response for this promotion.</p></div><button class="copy" type="button" id="copyPromo">Copy JSON</button></div><pre class="json">${jsonHtml(promoPayload(p))}</pre></div>`;
  }

  function renderDrawer(a, r){
    const sel = a.i, h = planHistory(a, r), gold = cssVar('--gold'), muted = cssVar('--muted');
    const comm = r.sell * r.p.comm, cost = r.p.cost || 0, scaleMax = Math.max(r.sell, a.h) * 1.05, W = v => v / scaleMax * 100;
    const wf = `<div class="wf">
        <div>Sell rate</div><div class="track"><div class="seg" style="left:0;width:${W(r.sell)}%;background:var(--panel-2);border:1px solid var(--line)"></div></div><div class="amt num">${fmt$(r.sell)}</div>
        <div>Commission ${Math.round(r.p.comm*100)}%</div><div class="track"><div class="seg" style="left:${W(r.sell-comm)}%;width:${W(comm)}%;background:var(--demand-bg);border:1px solid var(--demand)"></div></div><div class="amt num">−${fmt$(comm).slice(0)}</div>
        <div>Package cost</div><div class="track"><div class="seg" style="left:${W(r.net)}%;width:${W(cost)}%;background:var(--demand-bg);border:1px solid var(--demand)"></div></div><div class="amt num">−${fmt$(cost)}</div>
        <div><b>Net rate</b></div><div class="track"><div class="seg" style="left:0;width:${W(r.net)}%;background:${r.net>=a.h?'var(--healthy)':'var(--demand)'}"></div><div class="seg" style="left:${W(a.h)}%;width:2px;background:var(--text)"></div></div><div class="amt num"><b>${fmt$(r.net)}</b></div>
        <div>Hurdle</div><div class="sub" style="font-size:12px;color:var(--muted)">Black line. ${r.net >= a.h ? 'Net clears the hurdle.' : `Net is ${fmt$(a.h - r.net)} short of the hurdle.`}</div><div class="amt num">${fmt$(a.h)}</div>
      </div>`;
    const yMax = Math.max(4, Math.ceil(Math.max(...h.ty.map(p=>p[1]), ...h.ly.map(p=>p[1])) * 1.2));
    const dR = r.rooms - h.stlyRooms, dP = r.sell - h.stlyRate;
    return `
      <div class="section"><div class="kpis">
        <div class="kpi"><div class="k">Rooms on the books</div><div class="v num">${commonService.formateRoom(r.rooms)}</div><div class="d"><span class="${dR>0?'delta-up':dR<0?'delta-down':''}">${signedRoom(dR)}</span> vs. ${commonService.formateRoom(h.stlyRooms)} STLY</div></div>
        <div class="kpi"><div class="k">Sell rate</div><div class="v num">${fmt$(r.sell)}</div><div class="d"><span class="${dP>0?'delta-up':dP<0?'delta-down':''}">${dP>0?'+':dP<0?'−':''}${fmt$(Math.abs(dP))}</span> vs. ${fmt$(h.stlyRate)} STLY</div></div>
        <div class="kpi"><div class="k">Pickup, last 7 days</div><div class="v num">${signedRoom(h.pickup7)}</div><div class="d">STLY ${signedRoom(h.pickup7ly)}</div></div>
        <div class="kpi"><div class="k">Avg lead time</div><div class="v num">${commonService.formateRoom(Math.round(h.avgLead))} days</div><div class="d">STLY ${commonService.formateRoom(Math.round(h.avgLeadLy))} days</div></div>
      </div></div>
      <div class="section" style="padding-top:0;border-bottom:0"><button class="ghost" type="button" id="planRes">View reservations for this plan</button></div>
      <div class="section"><h3>Net rate build</h3><p class="hint">What the hotel keeps from this plan on this channel, against the hurdle for the date.</p>${wf}</div>
      ${r.margins ? `<div class="section"><h3>Length-of-stay check (R9a)</h3><p class="hint">Net minus hurdle for this plan on the nights around the stay date. A 2–3 night window that sums to zero or more gets MinLOS instead of a close.</p>
        <div class="tablewrap"><table style="min-width:0"><thead><tr><th>Night</th>${[-2,-1,0,1,2].map(o => `<th class="r">${o === 0 ? 'D' : (o > 0 ? 'D+' + o : 'D' + o)}</th>`).join('')}</tr></thead>
        <tbody><tr><td>Date</td>${[-2,-1,0,1,2].map(o => { const j = sel + o; return `<td class="r">${j >= 0 && j < activeDates.length ? md(activeDates[j].d) : '—'}</td>`; }).join('')}</tr>
        <tr><td>Net − hurdle</td>${[-2,-1,0,1,2].map(o => { const v = r.margins[o]; return `<td class="r num ${v == null ? '' : v >= 0 ? 'delta-up' : 'delta-down'}">${v == null ? '—' : (v >= 0 ? '+' : '') + fmt$(v)}</td>`; }).join('')}</tr></tbody></table></div>
        <p class="hint" style="margin-top:8px">${r.rule === 'R9a' ? `Result: MinLOS ${commonService.formateRoom(r.minLos)}.` : `Result: close. ${r.losWhy || ''}`}</p></div>` : ''}
      <div class="section"><h3>Plan pace</h3><p class="hint">Rooms on the books for this plan by days before arrival, vs. STLY.</p>
        <div class="legend"><span><i style="background:${gold}"></i>This year</span><span><i style="background:${muted}"></i>STLY</span></div>
        ${lineChart({
          series:[
            {pts:h.ly, color:muted, width:1.8, label:'STLY', key:'ly'},
            {pts:h.ty, color:gold, width:2.6, label:'This year', key:'ty'}
          ],
          yMax,
          yLabel: 'Rooms',
          marker: a.s.dba,
          h: 210,
          isOccupancy: false,
          capacity: activeCapacity,
          stayDate: a.s.d
        })}</div>
      <div class="section"><h3>Lead time mix</h3><p class="hint">Share of this plan's bookings by days before arrival. Shows whether closing it now would cut off its usual demand.</p>
        <div class="legend"><span><i style="background:${gold}"></i>This year</span><span><i style="background:${muted}"></i>STLY</span></div>
        ${groupedBars({labels:LEAD_BUCKETS, a:h.lead, b:h.leadLy, colorA:gold, colorB:muted, yLabel:'% of bookings by lead-time bucket (days)', h:180})}</div>
      <div class="section"><h3>Rule path</h3><p class="hint">Rules are checked in order. The highlighted step decided the action (${r.rule}).</p>
        <ol class="trace">${r.trace.map(t => `<li class="${t.hit?'hit':''}"><div><div class="q">${t.q}</div><div class="f">${esc(t.f||'')}</div></div><div class="val num">${esc(t.val)}</div></li>`).join('')}</ol></div>
      <div class="section"><div class="sec-head"><div><h3>Action payload</h3><p class="hint">Proposed record sent to the approval queue and channel manager.</p></div><button class="copy" type="button" id="copyPlan">Copy JSON</button></div>
        <pre class="json">${jsonHtml(planAction(a, r))}</pre></div>`;
  }

  screen();

  return {
    capacity: activeCapacity,
    SCR,
    P1,
    fmt$,
    fmtRoom,
    r2,
    signedRoom,
    dow,
    md,
    longDate,
    planKey,
    paceIndex,
    adrIndex,
    analyse,
    why,
    datePayload,
    planAction,
    reservations,
    promoPayload,
    promoForDate,
    buildCampaigns,
    rankRecord,
    renderPace,
    renderMix,
    renderTrace,
    renderSpec,
    renderPromoTab,
    renderDrawer,
    horizonDays,
    horizonText,
    activeDates,
    activePlans
  };
}
